/**
 * The TanStack implementation of `RouterAdapter` — the FIRST non-Next host to
 * supply one (the seam is `packages/ui/src/router.tsx`, RO-4-01).
 *
 * The seam exists so `packages/ui` never learns which router it is running
 * under: ~110 files there import `next/link` / `next/navigation` today, RO-4-02
 * migrates them onto `RouterLink` / `useNavigate` / `usePathname` / `useSearch`,
 * and this object is what makes those four resolve to TanStack instead of Next.
 * Nothing below may leak a TanStack type outward — the adapter's whole job is to
 * be the only file in the client that knows.
 *
 * Two mappings are worth stating because they are NOT one-to-one:
 *
 *   refresh()  Next's `router.refresh()` re-runs the server render for the
 *              current route. There is no server render here, so the honest
 *              equivalent is `router.invalidate()` — re-run the loaders. Data
 *              refetching proper belongs to SWR (PRD D3), which is untouched.
 *
 *   useSearch() The seam's contract is `URLSearchParams`, matching what
 *              `useSearchParams()` returned. TanStack's own `useSearch` returns
 *              a PARSED, VALIDATED object — the typed-search-schema win (D2/I9)
 *              — so this deliberately reads the raw `searchStr` instead. Typed
 *              access is what route code should use (see `shell/search.ts`);
 *              this untyped view exists only to keep the ~110 unmigrated call
 *              sites working during RO-4-02, and it should shrink to nothing.
 */

import * as React from "react";
import {
  Link as TanStackLink,
  useNavigate as useTanStackNavigate,
  useRouter as useTanStackRouter,
  useLocation,
} from "@tanstack/react-router";
import type { RouterAdapter, RouterLinkProps } from "@excellent/ui/router";

/**
 * `<a href>` semantics over TanStack's `<Link to>`. Call sites pass a plain
 * string href built elsewhere (a contract path, a section slug), never a typed
 * route object, so `to` is cast — the route-tree typing that cast gives up is
 * recovered where it matters, on search params, by `shell/search.ts`.
 */
const AdapterLink: React.ComponentType<RouterLinkProps> = ({ href, ...rest }) => (
  <TanStackLink to={href as never} {...rest} />
);

export const tanstackAdapter: RouterAdapter = {
  Link: AdapterLink,

  useNavigate() {
    const navigate = useTanStackNavigate();
    const router = useTanStackRouter();
    return React.useMemo(
      () => ({
        push: (href: string) => void navigate({ to: href as never }),
        replace: (href: string) => void navigate({ to: href as never, replace: true }),
        back: () => router.history.back(),
        refresh: () => void router.invalidate(),
      }),
      [navigate, router],
    );
  },

  usePathname: () => useLocation({ select: (l) => l.pathname }),

  // `searchStr` carries the leading `?` when non-empty; URLSearchParams accepts
  // it either way. Re-created per render like `useSearchParams()` was.
  useSearch: () => new URLSearchParams(useLocation({ select: (l) => l.searchStr })),
};
